import {
  Component,
  inject,
  effect,
  computed,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MultiSelectComponent } from '../../shared/components/ui/multi-select/multi-select.component';
import type { FilterOption } from '../../shared/components/ui/multi-select/multi-select.component';
import { TableComponent } from '../../shared/components/ui/table/table.component';
import { TablePaginationComponent } from '../../shared/components/ui/table/table-pagination.component';
import { TableListHeaderComponent } from '../../shared/components/ui/table/table-list-header/table-list-header.component';
import { HorusLoadingComponent } from '../../shared/components/domain/horus-loading/horus-loading.component';
import { HorusMessageGeneralComponent } from '../../shared/components/domain/horus-message-general/horus-message-general.component';
import { MessagingState } from './services/messaging.state';
import { Message, MessageType } from './models/messaging.models';
import {
  LucideAngularModule,
  Eye,
  RotateCw,
  MessageCircle,
} from 'lucide-angular';
import { MessageTypeLabelComponent } from '../../shared/components/domain/message-type-label/message-type-label.component';
import { MatIconModule } from '@angular/material/icon';
import { DialogManagerService } from '../../shared/services/dialog-manager.service';
import { RouteStateService } from '../../core/services/route-state.service';
import { OdMessagingTimelineDialogComponent } from '../order-detail/components/od-messaging-timeline-dialog/od-messaging-timeline-dialog.component';
import { OdMessagingResendDialogComponent } from '../order-detail/components/od-messaging-resend-dialog/od-messaging-resend-dialog.component';
import {
  MessagingData,
  MessagingResendData,
} from '../order-detail/models/order-messaging.model';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  createAngularTable,
} from '@tanstack/angular-table';
import dayjs from 'dayjs';

const columnHelper = createColumnHelper<Message>();

@Component({
  selector: 'app-messaging',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MultiSelectComponent,
    TableComponent,
    TablePaginationComponent,
    TableListHeaderComponent,
    HorusLoadingComponent,
    HorusMessageGeneralComponent,
    LucideAngularModule,
    MessageTypeLabelComponent,
    MatIconModule,
  ],
  templateUrl: './messaging.component.html',
  styleUrls: ['./messaging.component.scss'],
})
export class MessagingComponent {
  readonly state = inject(MessagingState);
  private readonly dialogManagerService = inject(DialogManagerService);
  private readonly routeState = inject(RouteStateService);

  readonly Eye = Eye;
  readonly RotateCw = RotateCw;
  readonly MessageCircle = MessageCircle;

  readonly descriptionCellTemplate = viewChild<TemplateRef<any>>(
    'descriptionCellTemplate'
  );
  readonly typeCellTemplate = viewChild<TemplateRef<any>>('typeCellTemplate');
  readonly actionsCellTemplate = viewChild<TemplateRef<any>>(
    'actionsCellTemplate'
  );

  readonly messageTypeOptions: FilterOption[] = [
    { label: 'Correo', value: 'EMAIL' },
    { label: 'SMS', value: 'SMS' },
    { label: 'Whatsapp', value: 'Whatsapp' },
  ];

  // Form control - initialized with all values to show "Todos" by default
  typeControl = new FormControl<string[]>(
    this.messageTypeOptions.map((option) => option.value)
  );

  readonly columns = computed(() => {
    return [
      columnHelper.accessor('templateName', {
        header: 'Nombre',
        cell: (info) => info.getValue(),
        size: 158,
        meta: {
          className: 'nombre-cell-wrapper',
        },
      }),
      columnHelper.accessor('asunto', {
        header: 'Descripción',
        cell: () => this.descriptionCellTemplate(),
        size: 350,
        meta: {
          className: 'descripcion-cell-wrapper',
        },
      }),
      columnHelper.accessor('fecha', {
        header: 'Fecha',
        cell: (info) => this.getFormattedDate({ fecha: info.getValue() }),
        size: 72,
        meta: {
          className: 'fecha-cell-wrapper',
        },
      }),
      columnHelper.accessor('fecha', {
        id: 'hora',
        header: 'Hora',
        cell: (info) => this.getFormattedHour({ fecha: info.getValue() }),
        size: 62,
        meta: {
          className: 'hora-cell-wrapper',
        },
      }),
      columnHelper.accessor('destinatario', {
        header: 'Destinatario',
        cell: (info) => info.getValue(),
        size: 188,
        meta: {
          className: 'destinatario-cell-wrapper',
        },
      }),
      columnHelper.accessor('tipoMensaje', {
        header: 'Tipo',
        cell: () => this.typeCellTemplate(),
        size: 101,
        meta: {
          className: 'tipo-cell-wrapper',
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Acciones',
        cell: () => this.actionsCellTemplate(),
        size: 86,
        meta: {
          className: 'acciones-cell-wrapper',
        },
      }),
    ];
  });

  readonly table = computed(() => {
    const messages = this.state.filteredMessages();

    return createAngularTable(() => ({
      data: messages,
      columns: this.columns(),
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      enableSorting: false,
      enableRowSelection: false,
      initialState: {
        pagination: {
          pageSize: this.state.pageSize(),
        },
      },
    }));
  });

  constructor() {
    effect(() => {
      const types = this.state.selectedTypes();
      const allTypes = this.messageTypeOptions.map((option) => option.value);

      if (allTypes.every((type) => types.includes(type as MessageType))) {
        this.typeControl.setValue([...allTypes], { emitEvent: false });
      } else {
        this.typeControl.setValue([...types], { emitEvent: false });
      }
    });
  }

  onTypeFilterChange(selectedTypes: string[]) {
    this.state.setSelectedTypes(selectedTypes as MessageType[]);
  }

  viewDetails(message: Message, event?: MouseEvent) {
    const data = this.mapToMessagingData(message);
    this.dialogManagerService.openDrawer(
      OdMessagingTimelineDialogComponent,
      data,
      event
    );
  }

  resendMessage(message: Message, event?: MouseEvent) {
    const data = this.mapToMessagingResendData(message);
    this.dialogManagerService.openConfirm(
      OdMessagingResendDialogComponent,
      data,
      event
    );
  }

  getFormattedHour(message: any): string {
    const date = message.fecha;

    if (!date) {
      return '-';
    }

    const parsedDate = dayjs(date, 'DD/MM/YYYY HH:mm:ss');

    if (!parsedDate.isValid()) {
      return '-';
    }

    return parsedDate.format('hh:mm:ss A');
  }

  getFormattedDate(message: any): string {
    const date = message.fecha;

    if (!date) {
      return '-';
    }

    const parsedDate = dayjs(date, 'DD/MM/YYYY HH:mm:ss');

    if (!parsedDate.isValid()) {
      return '-';
    }

    return parsedDate.format('DD/MM/YYYY');
  }

  getTypeBadgeClass(type: string): string {
    const typeUpper = type?.toUpperCase();
    switch (typeUpper) {
      case 'EMAIL':
      case 'CORREO':
        return 'type-email';
      case 'SMS':
        return 'type-sms';
      case 'WHATSAPP':
        return 'type-whatsapp';
      default:
        return '';
    }
  }

  formatMessageType(type: string): string {
    const typeUpper = type?.toUpperCase();
    switch (typeUpper) {
      case 'EMAIL':
      case 'CORREO':
        return 'Correo';
      case 'SMS':
        return 'SMS';
      case 'WHATSAPP':
        return 'Whatsapp';
      default:
        return type || '';
    }
  }

  /**
   * Maps Message to MessagingData for timeline drawer
   */
  private mapToMessagingData(message: Message): MessagingData {
    return {
      tenantId: this.routeState.getRouteParam('tenantId')?.toLowerCase() || '',
      emailId: message.emailId,
      messageType: message.tipoMensaje,
      orderId: message.orderId || '',
      to: message.destinatario,
    };
  }

  /**
   * Maps Message to MessagingResendData for resend dialog
   */
  private mapToMessagingResendData(message: Message): MessagingResendData {
    return {
      tenantId: this.routeState.getRouteParam('tenantId')?.toLowerCase() || '',
      emailId: message.emailId,
      messageType: message.tipoMensaje,
      to: message.destinatario,
    };
  }

  clearFilters(): void {
    const allTypes = this.messageTypeOptions.map((option) => option.value);
    this.typeControl.setValue(allTypes);
    this.state.setSelectedTypes(allTypes as MessageType[]);
  }
}
